/**
 * ui-renderers/chat.ts
 * Chat message bubble rendering, typing indicator, waveform context, and welcome message.
 */

import { state, MAX_MSGS, saveReactions } from '../state';
import { dom, triggerEdgeGlow } from '../dom';
import { sendFeedback } from '../api';
import { renderMarkdown } from '../markdown';
import { startBrainwaveAnimation, stopBrainwaveAnimation } from '../effects';
import { escapeHtml, enhanceCodeBlocks } from './core';

interface AppendMessageOpts {
  empathy?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal: reaction controls
// ─────────────────────────────────────────────────────────────────────────────

function attachReactionControls(msgEl: HTMLElement, msgId: string) {
  if (!msgId) return;
  const bodyWrap = msgEl.querySelector('.ai-msg-body-wrap');
  if (!bodyWrap) return;
  const feedbackRow = bodyWrap.querySelector('.ai-msg-feedback');

  const reactBtn = document.createElement('button');
  reactBtn.className = 'ai-reaction-btn';
  reactBtn.innerHTML = '&#128578;';
  reactBtn.title = 'Add reaction';

  reactBtn.addEventListener('click', function (e) {
    e.stopPropagation();

    const existing = document.querySelector('.ai-emoji-picker');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    const picker = document.createElement('div');
    picker.className = 'ai-emoji-picker';

    const btnRect = reactBtn.getBoundingClientRect();
    picker.style.cssText = [
      'position:fixed',
      `bottom:${Math.round(window.innerHeight - btnRect.top + 6)}px`,
      `left:${Math.max(8, Math.round(btnRect.left))}px`,
      'z-index:9500',
    ].join(';');

    ['\uD83D\uDC4D', '\u2764\uFE0F', '\uD83C\uDF89', '\uD83D\uDE2E', '\uD83D\uDC4E'].forEach(emoji => {
      const btn = document.createElement('button');
      btn.textContent = emoji;
      btn.type = 'button';
      btn.addEventListener('click', () => {
        state.reactions[msgId] = emoji;
        saveReactions();
        const old = msgEl.querySelector('.ai-reaction-badge');
        if (old && old.parentNode) old.parentNode.removeChild(old);
        const badge = document.createElement('span');
        badge.className = 'ai-reaction-badge';
        badge.textContent = emoji;
        if (reactBtn.parentNode) reactBtn.parentNode.insertBefore(badge, reactBtn.nextSibling);
        if (picker.parentNode)   picker.parentNode.removeChild(picker);
        if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
      });
      picker.appendChild(btn);
    });

    document.body.appendChild(picker);

    const dismiss = (ev: MouseEvent) => {
      if (!picker.contains(ev.target as Node)) {
        if (picker.parentNode) picker.parentNode.removeChild(picker);
        document.removeEventListener('click', dismiss, true);
      }
    };
    const timer = setTimeout(() => {
      if (picker.parentNode) picker.parentNode.removeChild(picker);
      document.removeEventListener('click', dismiss, true);
    }, 5000);
    picker.addEventListener('click', () => clearTimeout(timer));
    setTimeout(() => document.addEventListener('click', dismiss, true), 0);
  });

  if (feedbackRow) feedbackRow.appendChild(reactBtn);
  else             bodyWrap.appendChild(reactBtn);

  if (state.reactions[msgId]) {
    const badge = document.createElement('span');
    badge.className = 'ai-reaction-badge';
    badge.textContent = state.reactions[msgId];
    if (reactBtn.parentNode) reactBtn.parentNode.insertBefore(badge, reactBtn.nextSibling);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Appends a message bubble to the chat.
 *
 * Self-Evolve v2 options:
 *   opts.empathy  — if provided, prepends a styled empathy line above the body
 */
export function appendMessage(
  role: 'user' | 'bot',
  text: string,
  msgId?: string,
  opts: AppendMessageOpts = {},
) {
  const container = dom.msgs;
  if (!container) return null;

  state.msgCount++;
  if (state.msgCount > MAX_MSGS) {
    const first = container.querySelector('.ai-msg');
    if (first) { container.removeChild(first); state.msgCount--; }
  }

  const wrap = document.createElement('div');
  wrap.className = 'ai-msg ai-msg-' + (role === 'user' ? 'user' : 'bot');
  wrap.dataset.msgId = msgId || '';

  const feedbackHtml = (role !== 'user' && msgId) ? [
    '<div class="ai-msg-feedback">',
      '<button class="ai-fb-btn" data-rating="1" aria-label="Helpful">&#128077;</button>',
      '<button class="ai-fb-btn" data-rating="-1" aria-label="Not helpful">&#128078;</button>',
    '</div>',
  ].join('') : '';

  const empathyHtml = (role !== 'user' && opts.empathy)
    ? '<div class="ai-empathy-line">' + escapeHtml(opts.empathy) + '</div>'
    : '';

  const bodyContent = role === 'user'
    ? escapeHtml(text)
    : (empathyHtml + renderMarkdown(text));

  wrap.innerHTML = [
    '<div class="ai-msg-avatar" aria-hidden="true">' + (role === 'user' ? 'ME' : 'AI') + '</div>',
    '<div class="ai-msg-body-wrap">',
      '<div class="ai-msg-body">' + bodyContent + '</div>',
      feedbackHtml,
    '</div>',
  ].join('');

  if (role !== 'user' && msgId) {
    wrap.querySelectorAll('.ai-fb-btn').forEach(btn => {
      const el = btn as HTMLElement;
      el.addEventListener('click', () => {
        sendFeedback(msgId, parseInt(el.dataset.rating ?? '', 10)).catch(() => {});
        wrap.querySelectorAll('.ai-fb-btn').forEach(b => b.classList.remove('voted'));
        el.classList.add('voted');
        if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);
      });
    });
    attachReactionControls(wrap, msgId);
  }

  container.appendChild(wrap);
  wrap.classList.add('entering');
  setTimeout(() => wrap.classList.remove('entering'), 350);
  container.scrollTop = container.scrollHeight;
  enhanceCodeBlocks(wrap);

  if (role !== 'user') triggerEdgeGlow();

  return wrap;
}

/**
 * Neural waveform typing indicator (Unified Frontend).
 * Replaces the legacy three-dot animation with a brainwave canvas.
 */
export function appendTypingIndicator(sentiment = 'neutral'): void {
  const msgs = dom.msgs;
  if (!msgs) return;

  const existing = msgs.querySelector('.ai-typing-neural');
  if (existing) existing.remove();

  const wrap = document.createElement('div');
  wrap.className = 'ai-msg ai-msg--bot ai-typing-neural';
  wrap.id        = 'ai-typing-indicator';
  wrap.innerHTML = `
    <canvas class="ai-brainwave-canvas" width="180" height="36"></canvas>
    <div class="ai-thinking-text">
      <span class="ai-thinking-context">Thinking</span>
      <span class="ai-thinking-dots">
        <span></span><span></span><span></span>
      </span>
    </div>`;

  msgs.appendChild(wrap);
  msgs.scrollTop = msgs.scrollHeight;

  const canvas = wrap.querySelector('.ai-brainwave-canvas') as HTMLCanvasElement;
  startBrainwaveAnimation(canvas, sentiment);
}

export function removeTypingIndicator(): void {
  stopBrainwaveAnimation();
  const el = document.getElementById('ai-typing-indicator');
  if (el) el.remove();
}

/**
 * Updates the context label inside the active waveform indicator.
 * Called from handlers.ts on `tool_call_start` events.
 */
export function setWaveformContext(label: string): void {
  const ctx = document.querySelector<HTMLElement>('.ai-thinking-context');
  if (ctx) ctx.textContent = label;
}

export function showWelcome() {
  let role = '', firstName = '';
  try {
    const tok = (typeof AkiliPortal !== 'undefined' && AkiliPortal.getToken) ? AkiliPortal.getToken() : '';
    if (tok) {
      const payload = JSON.parse(atob(tok.split('.')[1]));
      role = payload.role || '';
      const email = payload.email || '';
      firstName = email.split('@')[0].split('.')[0];
      firstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
    }
  } catch (_) {}

  let greeting, body;
  if (role === 'admin') {
    greeting = firstName ? 'Sawa, ' + firstName + '.' : 'Sawa.';
    body = " I'm Kili — I know this platform front to back. Matters, billing, team setup, reports — ask me anything and I'll give you a straight answer. What do you need?";
  } else if (role === 'advocate') {
    greeting = firstName ? 'Habari, ' + firstName + '.' : 'Habari.';
    body = " I'm Kili. I can check your deadlines, draft client updates, summarise matters, and walk you through anything on the platform. No waffle — just answers. What's on your plate?";
  } else if (role === 'client') {
    greeting = firstName ? 'Habari, ' + firstName + '.' : 'Habari.';
    body = " I'm Kili — I'm here to help you track your case, find documents, and understand how this portal works. For legal advice, your advocate is the one to call. For everything else, I've got you. What would you like to know?";
  } else {
    greeting = 'Habari.';
    body = " I'm Kili — your guide to Akili Suite. Ask me anything about the platform and I'll get you sorted.";
  }

  appendMessage('bot', greeting + body);
}
